// imports react and next
import React, { useEffect, useState } from "react";
import { Image as DatoImage } from "react-datocms";
import dynamic from "next/dynamic";
import { useDispatch } from "react-redux";
import Link from "next/link";

//import redux action
import store from "@/redux/store";
import {
  fetchLabelsAndConfigData,
  setLabelsConfigsData,
} from "@/redux/actions/labelsAndConfigActions";
import { fetchUsefulInformationData } from "@/redux/actions/usefulInformationActions";

//import graphql queries
import { LABEL_AND_CONFIG_QUERY } from "@/graphql/settingsQuery";
import { USEFUL_INFORMATION_QUERY } from "@/graphql/usefulInformation";

// imports utilities
import { isEmpty } from "@/utils/functions";

//import components
const MetaTags = dynamic(() => import("@/common/MetaTags"));
const GridSection = dynamic(() => import("@/components/Section/GridSection"));
const StickyScrollBar = dynamic(() => import("@/components/StickyScrollBar"));
const MainSlider = dynamic(() => import("@/components/Sliders"));
const AbstractCard = dynamic(() => import("@/components/Card"));
const ContainerWithShadow = dynamic(() => import("@/components/Section/ContainerWithShadow"));
const IntroSectionInner = dynamic(() => import("@/components/Section/IntroSectionInner"));
const Breadcrumbs = dynamic(() => import("@/components/Breadcrumbs"));

const UsefulInformation = (props) => {
  const { labelsConfigData, usefulInfoPageData } = props;

  const dispatch = useDispatch();

  const [siteConfig, setSiteConfig] = useState({});
  const [labelsList, setLabelslist] = useState({});

  useEffect(() => {
    dispatch(setLabelsConfigsData(labelsConfigData));
    setLabelslist(labelsConfigData?.buttonsAndLabelsPage);
    setSiteConfig(labelsConfigData?.siteConfig);
  }, [dispatch, labelsConfigData]);

  return (
    <>
      <MetaTags
        metaTags={usefulInfoPageData?._seoMetaTags}
        seoData={usefulInfoPageData?.seo}
        noFollow={usefulInfoPageData?.nofollow}
        noIndex={usefulInfoPageData?.noindex}
      />

      <StickyScrollBar />

      <div className="relative w-full">
        <MainSlider sliderData={usefulInfoPageData?.gallery} siteConfig={siteConfig} />
      </div>

      <ContainerWithShadow containerWidthClass="container" noShadow="true">
        <div>
          <Breadcrumbs
            items={[
              {
                label: "Home",
                path: "/",
              },
              {
                label: usefulInfoPageData?.title,
              },
            ]}
          />
          <IntroSectionInner
            title={usefulInfoPageData?.subtitle}
            text={usefulInfoPageData?.description}
          />
        </div>
      </ContainerWithShadow>

      <div className="bg-[#F6F6F6] w-full">
        <ContainerWithShadow
          containerWidthClass=""
          containerHeightClass="h-auto"
          noShadow="true"
          className="flex gap-x-[70px] gap-y-[40px] flex-wrap justify-center items-center"
        >
          {usefulInfoPageData?.logosGallery?.map((item, index) => {
            const Tag = item.customData.link ? Link : "div";
            return (
              <Tag
                href={item?.customData?.link}
                rel="noreferrer noopener"
                target="_blank"
                key={index}
              >
                <DatoImage
                  data={{
                    src: item?.responsiveImage?.src,
                    alt: item?.alt || "location",
                    title: item?.title || `Location ${index + 1}`,
                    width: 100,
                    height: 100,
                    placeholder: !isEmpty(item?.blurUpThumb) ? "blur" : "empty",
                    blurDataURL: item?.blurUpThumb,
                    className: "lazyload",
                  }}
                />
              </Tag>
            );
          })}
        </ContainerWithShadow>
      </div>

      {!isEmpty(usefulInfoPageData?.attachmentCards) && (
        <>
          <GridSection
            containerWidthClass="w-full"
            containerGridClass="lg:grid-cols-3 sm:grid-cols-2 xxs:grid-cols-1 sm:mx-[40px] lg:mx-0"
            containerGridGap="gap-6"
          >
            {usefulInfoPageData?.attachmentCards?.map((item, index) => (
              <AbstractCard
                key={index}
                title={item?.title}
                icon={item?.icon}
                abstract={item?.abstract}
                btnUrl={
                  item?.attachment?.url ??
                  usefulInfoPageData.attachmentCardsWithFallbackLocale[index]?.attachment?.url
                }
                btnLabel={labelsList?.download}
                bgColor={item?.cardColor?.hex?.toLowerCase() || "#A6C7EA"}
              />
            ))}
          </GridSection>
        </>
      )}
    </>
  );
};

export default UsefulInformation;

export async function getStaticProps({ preview = false, locale = "en" }) {
  let requestParams = USEFUL_INFORMATION_QUERY?.replace(/\lang/g, locale);

  const usefulInfoResponse = await store.dispatch(
    fetchUsefulInformationData(requestParams, preview),
  );
  const labelsConfigData = await store.dispatch(
    fetchLabelsAndConfigData(LABEL_AND_CONFIG_QUERY, preview, {
      lang: locale,
    }),
  );
  return {
    props: {
      preview,
      usefulInfoPageData: usefulInfoResponse?.usefulInformation || {},
      // serviceSubpageData: usefulInfoResponse?.serviceSubpage || {},
      labelsConfigData: labelsConfigData || {},
    },
  };
}
